import { Button, IconButton } from '@/components/common/Button';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    CheckCircle,
    LightBulb04,
    PlusCircle,
    Trash03,
} from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import { Center, Divider, FlexProps, Spacer, Spinner } from '@chakra-ui/react';
import { OnboardingInfoBeneficiary } from '@common/types';
import { sum } from '@common/util/arrays';
import { dt } from '@common/util/dateTime';
import { isBoolean } from '@common/util/types';
import { useCallback, useState } from 'react';
import { showBeneficiaryModal } from './BeneficiaryModal';
import { labelByRelationship, mapEqualFractions } from './helpers';
import { useOpenIraFlowContext } from './OpenIraFlow';
import OpenIraFlowStep from './OpenIraFlowStep';

const REMOVE_BUTTON_CONTAINER_WIDTH = 12;

const OpenIraFlowBeneficiaryStep = () => {
    const { setStep, info, updateInfo } = useOpenIraFlowContext();
    const [primaryBeneficiaries, setPrimaryBeneficiaries] = useState(
        info?.primaryBeneficiaries ?? []
    );
    const [secondaryBeneficiaries, setSecondaryBeneficiaries] = useState(
        info?.secondaryBeneficiaries ?? []
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!info) {
        return (
            <Center h="full" w="full">
                <Spinner color="gray.400" />
            </Center>
        );
    }

    const isAttributionValid = getIsAttributionValid({
        primaryBeneficiaries,
        secondaryBeneficiaries,
    });

    return (
        <OpenIraFlowStep
            headerTitle="Add a beneficiary"
            onBack={() => {
                setIsSubmitting(true);
                updateInfo({
                    ...info,
                    primaryBeneficiaries,
                    secondaryBeneficiaries,
                })
                    .then(() => setStep('INTRO'))
                    .finally(() => setIsSubmitting(false));
            }}
            onNext={() => {
                setIsSubmitting(true);
                updateInfo({
                    ...info,
                    primaryBeneficiaries,
                    secondaryBeneficiaries,
                    lastCompletedRetirementStep: 'BENEFICIARY',
                })
                    .then(() => setStep('PORTFOLIO'))
                    .finally(() => setIsSubmitting(false));
            }}
            isNextDisabled={
                primaryBeneficiaries.length === 0 || !isAttributionValid
            }
            isSubmitting={isSubmitting}
            footerMiddleContent={
                isBoolean(isAttributionValid) ? (
                    <>
                        <Spacer />
                        <BeneficiaryAttributionValidation
                            isValid={isAttributionValid}
                            mr={12}
                            justifySelf="flex-end"
                        />
                    </>
                ) : (
                    <></>
                )
            }
        >
            <Paragraph textColor="textMuted" mb={12}>
                A beneficiary will inherit the assets in your account in the
                event of your death. Assigning at least one beneficiary is
                required to help transition your assets quickly and without
                delay.
            </Paragraph>
            <FlexRow gap={16}>
                <FlexCol grow={1}>
                    <BeneficiarySection
                        title={
                            <Text fontWeight="semibold">
                                Primary beneficiary
                            </Text>
                        }
                        addModalTitle="Add a primary beneficiary"
                        editModalTitle="Edit a primary beneficiary"
                        beneficiaries={primaryBeneficiaries}
                        setBeneficiaries={setPrimaryBeneficiaries}
                        showAddButton
                    />
                    <FlexRow>
                        <Divider my={8} mr={REMOVE_BUTTON_CONTAINER_WIDTH} />
                    </FlexRow>
                    <BeneficiarySection
                        title={
                            <Text fontWeight="semibold">
                                Secondary beneficiary
                                <Text
                                    color="textMuted"
                                    fontWeight="normal"
                                    ml={2}
                                >
                                    (Optional)
                                </Text>
                            </Text>
                        }
                        addModalTitle="Add a secondary beneficiary"
                        editModalTitle="Edit a secondary beneficiary"
                        beneficiaries={secondaryBeneficiaries}
                        setBeneficiaries={setSecondaryBeneficiaries}
                        // Show "Add" button after primary beneficiary added
                        showAddButton={primaryBeneficiaries.length > 0}
                    />
                </FlexCol>
                <BeneficiarySidebar minW={220} maxW={220} />
            </FlexRow>
        </OpenIraFlowStep>
    );
};

const BeneficiarySection = ({
    title,
    addModalTitle,
    editModalTitle,
    beneficiaries,
    setBeneficiaries,
    showAddButton,
}: {
    title: JSX.Element;
    addModalTitle: string;
    editModalTitle: string;
    beneficiaries: OnboardingInfoBeneficiary[];
    setBeneficiaries: (beneficiaries: OnboardingInfoBeneficiary[]) => void;
    showAddButton: boolean;
}) => {
    const save = useCallback(
        (savedBeneficiary: OnboardingInfoBeneficiary) => {
            setBeneficiaries(
                beneficiaries.find(({ id }) => id === savedBeneficiary.id)
                    ? // found, so edit
                      beneficiaries.map((b) =>
                          b.id === savedBeneficiary.id ? savedBeneficiary : b
                      )
                    : // not found, so add
                      [...beneficiaries, savedBeneficiary].map(
                          mapEqualFractions
                      )
            );
        },
        [beneficiaries, setBeneficiaries]
    );

    const remove = useCallback(
        (removedBeneficiaryId: string) => {
            setBeneficiaries(
                beneficiaries
                    .filter(({ id }) => id !== removedBeneficiaryId)
                    .map(mapEqualFractions)
            );
        },
        [beneficiaries, setBeneficiaries]
    );

    return (
        <FlexCol>
            <FlexRow mb={4}>
                {title}
                <Spacer />
                {showAddButton && beneficiaries.length < 10 && (
                    <Button
                        variant="link"
                        leftIcon={<PlusCircle />}
                        mr={REMOVE_BUTTON_CONTAINER_WIDTH}
                        onClick={() => {
                            showBeneficiaryModal({
                                title: addModalTitle,
                                save,
                            });
                        }}
                        onClickEvent="Add Beneficiary Button Clicked"
                    >
                        Add
                    </Button>
                )}
            </FlexRow>
            {beneficiaries.map((beneficiary) => (
                <BeneficiaryListItem
                    key={beneficiary.id}
                    beneficiary={beneficiary}
                    editModalTitle={editModalTitle}
                    save={save}
                    remove={remove}
                />
            ))}
        </FlexCol>
    );
};

const BeneficiaryListItem = ({
    beneficiary,
    editModalTitle,
    save,
    remove,
}: {
    beneficiary: OnboardingInfoBeneficiary;
    editModalTitle: string;
    save: (beneficiary: OnboardingInfoBeneficiary) => void;
    remove: (beneficiaryId: string) => void;
}) => {
    const { id, firstName, lastName, relationship, dob, fraction } =
        beneficiary;
    return (
        <FlexRow alignItems="center" my={4}>
            <FlexCol mt={2}>
                <Text fontSize="lg" mb={2}>
                    {firstName} {lastName}
                </Text>
                <FlexRow alignItems="center">
                    <Text fontSize="sm" mr={3}>
                        {labelByRelationship[relationship]},{' '}
                        {dt(dob).format('M/D/YY')}
                    </Text>
                    <LinkText
                        fontSize="sm"
                        onClick={() =>
                            showBeneficiaryModal({
                                title: editModalTitle,
                                save,
                                beneficiary,
                            })
                        }
                    >
                        Edit
                    </LinkText>
                </FlexRow>
            </FlexCol>
            <Spacer />
            <InputField
                id={id}
                name={id}
                label="% attributed"
                type="percent"
                inputOptions={{ maxDecimals: 0 }}
                value={fraction}
                onChange={(value) => {
                    save({ ...beneficiary, fraction: value });
                }}
                w={18}
                labelProps={{ whiteSpace: 'nowrap' }}
                inputProps={{ textAlign: 'right' }}
            />
            <FlexRow w={REMOVE_BUTTON_CONTAINER_WIDTH} justifyContent="center">
                <IconButton
                    size="xs"
                    variant="link"
                    icon={<Trash03 color="gray.400" />}
                    mt={5}
                    onClick={() => remove(beneficiary.id)}
                    onClickEvent="Remove Beneficiary Button Clicked"
                    aria-label="remove"
                />
            </FlexRow>
        </FlexRow>
    );
};

const BeneficiarySidebar = (props: FlexProps) => {
    return (
        <FlexCol {...props}>
            <LightBulb04 color="textMuted" mb={3} />
            <Paragraph fontSize="sm" color="textMuted" lineHeight="taller">
                A beneficiary is a person designated to receive what’s held in
                this account in the event of a death. Designated benficiaries
                will not have access to the account and will not be contacted
                before then.
            </Paragraph>
        </FlexCol>
    );
};

interface BeneficiaryAttributionValidationProps extends FlexProps {
    isValid: boolean;
}

const BeneficiaryAttributionValidation = ({
    isValid,
    ...props
}: BeneficiaryAttributionValidationProps) => {
    return (
        <FlexRow alignItems="center" {...props} gap={2}>
            <Paragraph fontSize="sm" color="textMuted" lineHeight="taller">
                Total attributed amounts must equal 100%
            </Paragraph>
            {isValid ? (
                <CheckCircle color="green.600" />
            ) : (
                <Trash03 color="red.500" />
            )}
        </FlexRow>
    );
};

const getIsAttributionValid = ({
    primaryBeneficiaries,
    secondaryBeneficiaries,
}: {
    primaryBeneficiaries: OnboardingInfoBeneficiary[];
    secondaryBeneficiaries: OnboardingInfoBeneficiary[];
}): boolean | null => {
    if (!primaryBeneficiaries.length && !secondaryBeneficiaries.length) {
        return null;
    }

    const isPrimaryValid =
        primaryBeneficiaries.length === 0 ||
        sum(primaryBeneficiaries, (b) => b.fraction).toFixed(4) === '1.0000';
    const isSecondaryValid =
        secondaryBeneficiaries.length === 0 ||
        sum(secondaryBeneficiaries, (b) => b.fraction).toFixed(4) === '1.0000';

    return isPrimaryValid && isSecondaryValid;
};

export default OpenIraFlowBeneficiaryStep;
